import { DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegionalFilterChange } from '@features/regional-overview/components/regional-risk-filter-select/use-filter-changed';
import type { DateRangeFilterValue, IRegionalRiskFilter } from '@features/regional-overview/regional-overview.models';
import FilterSection from '../filter-section/filter-section.component';
import styles from './range-picker-filter.module.css';

const { RangePicker } = DatePicker;

type RangePickerFilterProps<T extends string> = Omit<IRegionalRiskFilter<T>, 'items' | 'width'> & {
  defaultExpanded?: boolean;
};
const RangePickerFilterComponent = <T extends string>({
  title,
  filter,
  selectedValue,
  disabled,
  disabledTooltip,
  dataTestID,
  defaultExpanded,
  onFilterChanged
}: RangePickerFilterProps<T>) => {
  const [t] = useTranslation();
  const handleFilterChanged = useRegionalFilterChange(filter, onFilterChanged);

  const committed = selectedValue as DateRangeFilterValue;
  const [draftStart, setDraftStart] = useState<Dayjs | null>(committed?.[0] ?? null);
  const [draftEnd, setDraftEnd] = useState<Dayjs | null>(committed?.[1] ?? null);
  const [lastCommitted, setLastCommitted] = useState<DateRangeFilterValue>(committed);

  if (committed !== lastCommitted) {
    setLastCommitted(committed);
    setDraftStart(committed?.[0] ?? null);
    setDraftEnd(committed?.[1] ?? null);
  }

  const commit = (start: Dayjs | null, end: Dayjs | null): void => {
    if (start && end) {
      handleFilterChanged([start, end]);
    } else {
      handleFilterChanged(undefined);
    }
  };

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null): void => {
    if (dates?.[0] && dates?.[1]) {
      handleFilterChanged([dates[0], dates[1]]);
    } else {
      handleFilterChanged(undefined);
    }
  };

  const handleStartChange = (date: Dayjs | null): void => {
    setDraftStart(date);
    commit(date, draftEnd);
  };

  const handleEndChange = (date: Dayjs | null): void => {
    setDraftEnd(date);
    commit(draftStart, date);
  };

  return (
    <FilterSection
      name={title ?? ''}
      selectedText=''
      disabled={disabled}
      disabledTooltip={disabledTooltip}
      defaultExpanded={defaultExpanded}
      dataTestId={dataTestID}>
      <div className={styles.desktopPicker}>
        <RangePicker value={committed} onChange={handleRangeChange} />
      </div>
      <div className={styles.mobilePickers}>
        <DatePicker
          inputReadOnly
          style={{ width: '100%' }}
          value={draftStart}
          onChange={handleStartChange}
          placeholder={t('pages.regional_overview.regional_risk.start_date')}
          disabledDate={current => (draftEnd ? current.isAfter(draftEnd, 'day') : false)}
          data-testid={dataTestID ? `${dataTestID}-start` : undefined}
        />
        <DatePicker
          inputReadOnly
          style={{ width: '100%' }}
          value={draftEnd}
          onChange={handleEndChange}
          placeholder={t('pages.regional_overview.regional_risk.end_date')}
          disabledDate={current => (draftStart ? current.isBefore(draftStart, 'day') : false)}
          data-testid={dataTestID ? `${dataTestID}-end` : undefined}
        />
      </div>
    </FilterSection>
  );
};

export default RangePickerFilterComponent;
